import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  DollarSign, ShoppingBag, Users, TrendingUp, Calendar,
  Store, Package,
} from 'lucide-react';
import { format } from 'date-fns';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { adminApi } from '@/services/api';
import { formatNPRCompact, formatNPR } from '@/utils/format';
import { cn } from '@nepal-ecom/ui';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@nepal-ecom/ui';

type Range = '7d' | '30d' | '90d' | '1y';

const RANGE_LABEL: Record<Range, string> = {
  '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', '1y': 'Last 12 months',
};

interface AnalyticsResponse {
  range: Range;
  series: Array<{ date: string; gmv: number; orders: number; customers: number }>;
  totals: { gmv: number; orders: number; customers: number; aov: number };
  provinceShare: Array<{ name: string; value: number }>;
  paymentMix: Array<{ method: string; value: number }>;
  topVendors: Array<{ id: string; name: string; orders: number; gmv: number; growth: number }>;
  topCategories: Array<{ name: string; sales: number }>;
}

const PROVINCE_COLORS = ['#D95100', '#F59E0B', '#0EA5E9', '#10B981', '#8B5CF6', '#F472B6', '#64748B'];

const PAYMENT_LABEL: Record<string, string> = {
  esewa: 'eSewa', khalti: 'Khalti', cod: 'COD', wallet: 'Wallet',
};

interface KpiProps { icon: React.ElementType; label: string; value: string; tone: string; loading: boolean; }

function Kpi({ icon: Icon, label, value, tone, loading }: KpiProps) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center', tone)}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
          {loading
            ? <Skeleton className="h-7 w-24 mt-1" />
            : <p className="text-2xl font-bold text-slate-900 tabular-nums mt-1">{value}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function Analytics() {
  const [range, setRange] = useState<Range>('30d');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-analytics', range],
    queryFn: () => adminApi.getPlatformAnalytics({ range }),
    select: (res) => res.data.data as AnalyticsResponse,
  });

  const totals = data?.totals ?? { gmv: 0, orders: 0, customers: 0, aov: 0 };
  const series = data?.series ?? [];
  const provinceShare = data?.provinceShare ?? [];
  const paymentMix = data?.paymentMix ?? [];
  const topCategories = data?.topCategories ?? [];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Platform Analytics"
        description="Marketplace performance, payment mix, and regional demand."
        actions={
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 shadow-xs">
            {(Object.keys(RANGE_LABEL) as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={cn(
                  'px-2.5 py-1 text-xs font-medium rounded transition-colors',
                  range === r ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100',
                )}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Kpi icon={DollarSign} label="GMV" value={formatNPRCompact(totals.gmv)} tone="bg-emerald-50 text-emerald-600" loading={isLoading} />
        <Kpi icon={ShoppingBag} label="Orders" value={totals.orders.toLocaleString()} tone="bg-cobalt-50 text-cobalt-600" loading={isLoading} />
        <Kpi icon={Users} label="Unique customers" value={totals.customers.toLocaleString()} tone="bg-primary-50 text-primary-600" loading={isLoading} />
        <Kpi icon={Calendar} label="Avg. order value" value={formatNPR(Math.round(totals.aov))} tone="bg-amber-50 text-amber-600" loading={isLoading} />
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>GMV & Orders trend</CardTitle>
            <p className="text-xs text-slate-500">{RANGE_LABEL[range]}</p>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <Skeleton className="h-[280px] w-full" />
          ) : series.length === 0 ? (
            <EmptyState icon={TrendingUp} title="No data yet" description="Orders will appear here once your storefront starts taking sales." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={series} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="gmv2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D95100" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#D95100" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="orders2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'dd MMM')} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tickFormatter={(v) => formatNPRCompact(v)} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={70} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, fontSize: 12, border: '1px solid #E2E8F0' }}
                  formatter={(v: number, name: string) => [name === 'gmv' ? formatNPRCompact(v) : v, name === 'gmv' ? 'GMV' : 'Orders']}
                  labelFormatter={(l) => format(new Date(l), 'dd MMM yyyy')}
                />
                <Area yAxisId="left" type="monotone" dataKey="gmv" stroke="#D95100" strokeWidth={2} fill="url(#gmv2)" />
                <Area yAxisId="right" type="monotone" dataKey="orders" stroke="#0EA5E9" strokeWidth={2} fill="url(#orders2)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3"><CardTitle>Sales by province</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[260px] w-full" />
            ) : provinceShare.length === 0 ? (
              <EmptyState icon={Store} title="No sales yet" description="Province breakdown appears once paid orders exist." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={provinceShare} dataKey="value" nameKey="name" innerRadius={56} outerRadius={92} paddingAngle={2}>
                    {provinceShare.map((_, i) => <Cell key={i} fill={PROVINCE_COLORS[i % PROVINCE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${v}%`} contentStyle={{ borderRadius: 10, fontSize: 12, border: '1px solid #E2E8F0' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle>Payment method mix</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[260px] w-full" />
            ) : paymentMix.length === 0 ? (
              <EmptyState icon={DollarSign} title="No transactions" description="Payment mix shows once customers check out." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={paymentMix.map((p) => ({ method: PAYMENT_LABEL[p.method] ?? p.method, value: p.value }))} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="method" tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v: number) => `${v}%`} contentStyle={{ borderRadius: 10, fontSize: 12, border: '1px solid #E2E8F0' }} />
                  <Bar dataKey="value" fill="#D95100" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2"><Package className="h-4 w-4 text-cobalt-600" /> Top categories</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {isLoading ? (
              <Skeleton className="h-[260px] w-full" />
            ) : topCategories.length === 0 ? (
              <EmptyState icon={Package} title="No category sales" description="Sales by category appears once orders exist." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={topCategories} layout="vertical" margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12, border: '1px solid #E2E8F0' }} />
                  <Bar dataKey="sales" fill="#0EA5E9" radius={[0, 6, 6, 0]} maxBarSize={20} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
